import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { 
  MessageSquare, 
  Mail, 
  Phone, 
  Calendar, 
  FolderOpen, 
  CheckSquare,
  CreditCard,
  Filter,
  StickyNote,
  User,
  Clock,
  FileText
} from 'lucide-react';

// axios instance already uses baseURL `${API_BASE_URL}/api`, so keep this empty
// to avoid double-prefix bugs like `/api/api/clients/...`.
const API_PREFIX = ``;

const ClientActivityFeed = ({ client, user, onSelectProject, onOpenTask, projects }) => {
  const [activities, setActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilters, setActiveFilters] = useState(['all']);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [expandedThreads, setExpandedThreads] = useState({});
  const [expandedEmails, setExpandedEmails] = useState({});

  // Add CSS for consistent widths
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .activity-feed-item {
        width: 800px !important;
        min-width: 800px !important;
        max-width: 800px !important;
        flex: none !important;
        box-sizing: border-box !important;
      }
      .activity-feed-item * {
        box-sizing: border-box !important;
      }
      .activity-feed-item .flex-1 {
        flex: 1 1 0% !important;
        min-width: 0 !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Activity type configurations - Only the types requested by the user
  const activityTypes = {
    note: { icon: StickyNote, label: 'Notes', color: 'bg-blue-500' },
    email: { icon: Mail, label: 'Emails', color: 'bg-green-500' },
    text: { icon: MessageSquare, label: 'Texts', color: 'bg-purple-500' },
    meeting: { icon: Calendar, label: 'Meetings', color: 'bg-orange-500' },
    project: { icon: FolderOpen, label: 'Projects', color: 'bg-indigo-500' },
    task: { icon: CheckSquare, label: 'Tasks', color: 'bg-emerald-500' },
    payment: { icon: CreditCard, label: 'Payments', color: 'bg-pink-500' },
    document: { icon: FileText, label: 'Documents', color: 'bg-gray-600' }
  };

  const normalizeActivityType = (rawType) => {
    const t = String(rawType || '').toLowerCase().trim();
    const map = {
      notes: 'note',
      note: 'note',
      emails: 'email',
      email: 'email',
      texts: 'text',
      text: 'text',
      sms: 'text',
      meetings: 'meeting',
      meeting: 'meeting',
      projects: 'project',
      project: 'project',
      tasks: 'task',
      task: 'task',
      payments: 'payment',
      payment: 'payment',
      invoices: 'payment',
      invoice: 'payment',
      documents: 'document',
      document: 'document',
      file: 'document',
      files: 'document'
    };
    return map[t] || 'note';
  };

  const fetchClientActivities = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');

      // Fetch all client activities from the new raw data endpoint
      const activitiesResponse = await api.get(`${API_PREFIX}/clients/${client.id}/activities`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          limit: 100, // Get more activities for better user experience
          offset: 0
        }
      });

      const rawActivities = activitiesResponse.data.activities || [];
      console.log(`✅ Fetched ${rawActivities.length} activities from raw data for client ${client.name}`);

      // Convert the activities to the expected format
      const activities = rawActivities.map(activity => ({
        id: activity.id,
        type: normalizeActivityType(activity.type),
        title: activity.title,
        description: activity.description,
        timestamp: activity.timestamp,
        author: activity.author,
        authorEmail: activity.author_email,
        data: activity.data || {},
        layout: activity.layout || 'center',
        source: activity.provider,
        direction: activity.direction,
        status: activity.status
      }));

      // Sort activities by timestamp (newest first) - should already be sorted from backend
      activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      setActivities(activities);
      setFilteredActivities(activities);
    } catch (error) {
      console.error('Failed to fetch client activities from raw data:', error);
      
      // Fallback: show empty state rather than breaking
      setActivities([]);
      setFilteredActivities([]);
    } finally {
      setLoading(false);
    }
  };

  // Real-time activity updates - poll for new activities every 30 seconds
  const pollForNewActivities = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token || !client?.id) return;

      const activitiesResponse = await api.get(`${API_PREFIX}/clients/${client.id}/activities`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          limit: 20, // Get recent activities only
          offset: 0
        }
      });

      const newRawActivities = activitiesResponse.data.activities || [];
      
      if (newRawActivities.length > 0) {
        // Check if we have newer activities than what's currently displayed
        const latestDisplayedTimestamp = activities.length > 0 ? new Date(activities[0].timestamp) : new Date(0);
        const latestNewTimestamp = new Date(newRawActivities[0].timestamp);
        
        if (latestNewTimestamp > latestDisplayedTimestamp) {
          // Refresh activities to show new data
          fetchClientActivities();
        }
      }
    } catch (error) {
      // Silently fail for polling - don't spam console
      console.debug('Activity polling failed:', error.message);
    }
  };

  // Set up real-time polling for activity updates
  useEffect(() => {
    if (!client?.id) return;

    // Set up polling interval for real-time updates
    const pollInterval = setInterval(pollForNewActivities, 30000); // Poll every 30 seconds

    return () => {
      clearInterval(pollInterval);
    };
  }, [client?.id, activities]); // Re-setup when client changes or activities change

  // Filter activities based on active filters
  const applyFilters = () => {
    if (activeFilters.includes('all')) {
      setFilteredActivities(activities);
    } else {
      const filtered = activities.filter(activity => 
        activeFilters.includes(activity.type)
      );
      setFilteredActivities(filtered);
    }
  };

  const toggleFilter = (filter) => {
    let newFilters;
    
    if (filter === 'all') {
      newFilters = ['all'];
    } else {
      newFilters = activeFilters.filter(f => f !== 'all');
      
      if (activeFilters.includes(filter)) {
        newFilters = newFilters.filter(f => f !== filter);
        if (newFilters.length === 0) {
          newFilters = ['all'];
        }
      } else {
        newFilters.push(filter);
      }
    }
    
    setActiveFilters(newFilters);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 168) { // Less than a week
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  // Handle clicking on activity items
  const handleActivityClick = (activity, event) => {
    try {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      if (activity?.type === 'email') {
        setExpandedEmails(prev => ({ ...prev, [activity.id]: !prev[activity.id] }));
        return;
      }

      setSelectedActivity(activity);
      setShowActivityModal(true);
    } catch (error) {
      console.error('❌ Error in handleActivityClick:', error);
    }
  };

  const ActivityItem = ({ activity }) => {
    const typeConfig = activityTypes[activity.type] || activityTypes.note;
    const IconComponent = typeConfig.icon;

    const baseItemClass = "activity-feed-item group flex items-start space-x-3 p-4 rounded-lg border-2 border-gray-200 bg-white shadow-sm hover:shadow-xl hover:border-purple-400 transition-all duration-200 cursor-pointer transform hover:scale-[1.01] select-none active:scale-[0.99]";

    if (activity.layout === 'center') {
      // Centered layout for events (meetings, tasks, notes, payments)
      return (
        <div className="w-full flex justify-center mb-4">
          <div 
            className={`${baseItemClass}`}
            onClick={(e) => handleActivityClick(activity, e)}
          >
            <div className={`flex-shrink-0 w-10 h-10 ${typeConfig.color} rounded-full flex items-center justify-center`}>
              <IconComponent className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-900 hover:text-purple-600">{activity.title}</h4>
                <span className="text-xs text-gray-500 flex-shrink-0">{formatTimestamp(activity.timestamp)}</span>
              </div>
              <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
              <div className="text-xs text-purple-600 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                Click to view details →
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      // Left/right justified layout for communications - Traditional messaging style
      const isRight = activity.layout === 'right';

      const isEmail = activity.type === 'email';
      const emailSubject = (activity.data?.subject || activity.title || '').trim();
      const emailFrom = (activity.data?.from || activity.authorEmail || '').trim();
      const emailTo = (activity.data?.to || '').trim();
      const emailPreview = (activity.data?.body_preview || activity.description || '').trim();
      const emailDirection = String(activity.direction || activity.data?.direction || '').toLowerCase();

      const senderLine = isEmail
        ? (emailDirection === 'outbound'
            ? `From: You${emailTo ? `  •  To: ${emailTo}` : ''}`
            : `From: ${emailFrom || client?.name || 'Unknown'}${emailTo ? `  •  To: ${emailTo}` : ''}`)
        : activity.title;

      const normalizedSubject = (emailSubject || '').toLowerCase();
      const cleanedPreview = (emailPreview || '')
        .replace(/^location\s+logo\s*\([^)]*\)\s*/i, '')
        .replace(/^email\s+from\s+unknown\s*/i, '')
        .trim();
      const previewSubjectFallback = cleanedPreview
        ? `${cleanedPreview.slice(0, 90)}${cleanedPreview.length > 90 ? '…' : ''}`
        : 'No subject';
      const headerSubject = (normalizedSubject.startsWith('email from ')
        ? ''
        : emailSubject) || previewSubjectFallback;
      const senderDisplay = senderLine;

      return (
        <div className={`w-full flex mb-4 ${isRight ? 'justify-end' : 'justify-start'}`}>
          <div
            className={`flex max-w-[75%] ${isRight ? 'flex-row-reverse' : 'flex-row'} items-end space-x-2 cursor-pointer group`}
            onClick={(e) => handleActivityClick(activity, e)}
            title="Click to view full message details"
          >
            {/* Avatar */}
            <div className={`flex-shrink-0 w-8 h-8 ${typeConfig.color} rounded-full flex items-center justify-center mb-1`}>
              <IconComponent className="w-4 h-4 text-white" />
            </div>
            
            {/* Message bubble */}
            {isEmail ? (
              <div className={`relative rounded-2xl border-2 border-purple-500 bg-white overflow-hidden shadow-sm ${
                isRight ? 'rounded-br-md' : 'rounded-bl-md'
              }`}>
                <div className="bg-purple-500 px-4 py-2">
                  <div className="text-sm font-semibold text-white">
                    {headerSubject}
                  </div>
                  <div className="text-[11px] text-purple-100 mt-1">
                    {senderDisplay}
                  </div>
                </div>

                {expandedEmails[activity.id] && (
                  <div className="px-4 py-3">
                    <div className="text-sm leading-relaxed text-gray-900 whitespace-pre-wrap">
                      {emailPreview || 'No preview available'}
                    </div>
                  </div>
                )}

                <div className="px-4 py-2 border-t border-purple-100 bg-white flex items-center justify-end">
                  <div className="text-xs text-gray-400">{formatTimestamp(activity.timestamp)}</div>
                </div>
              </div>
            ) : (
              <div className={`relative px-4 py-3 rounded-2xl shadow-sm ${
                isRight 
                  ? 'bg-purple-500 text-white rounded-br-md' 
                  : 'bg-gray-100 text-gray-900 rounded-bl-md'
              }`}>
                <div className={`text-xs font-medium mb-1 ${
                  isRight ? 'text-purple-100' : 'text-gray-500'
                }`}>
                  {activity.title}
                </div>
                <div className={`text-sm leading-relaxed ${
                  isRight ? 'text-white' : 'text-gray-900'
                }`}>
                  {activity.description || 'No message content'}
                </div>
                <div className={`text-xs mt-2 ${
                  isRight ? 'text-purple-200 text-right' : 'text-gray-400 text-left'
                }`}>
                  {formatTimestamp(activity.timestamp)}
                </div>
                <div className={`absolute top-3 w-3 h-3 transform rotate-45 ${
                  isRight ? 'bg-purple-500 -right-1' : 'bg-gray-100 -left-1'
                }`} />
              </div>
            )}
          </div>
        </div>
      );
    }
  };

  const getThreadedGroups = (items) => {
    const groups = [];
    const threadMap = new Map();

    for (const activity of items) {
      const isCommunication = activity.type === 'email' || activity.type === 'text';
      const threadKey = activity.data?.thread_key || activity.data?.conversation_key;

      if (!isCommunication || !threadKey) {
        groups.push({ kind: 'single', activity });
        continue;
      }

      // Prevent cross-type/source collisions (e.g., sms + email sharing same raw key)
      const groupKey = `${activity.type}:${activity.source || 'unknown'}:${threadKey}`;

      if (!threadMap.has(groupKey)) {
        threadMap.set(groupKey, []);
      }
      threadMap.get(groupKey).push(activity);
    }

    for (const [groupKey, threadItems] of threadMap.entries()) {
      threadItems.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      groups.push({
        kind: 'thread',
        threadKey: groupKey,
        type: threadItems[0].type,
        latest: threadItems[0],
        items: threadItems,
        count: threadItems.length
      });
    }

    // Sort all groups by latest timestamp desc
    groups.sort((a, b) => {
      const aTs = new Date(a.kind === 'thread' ? a.latest.timestamp : a.activity.timestamp);
      const bTs = new Date(b.kind === 'thread' ? b.latest.timestamp : b.activity.timestamp);
      return bTs - aTs;
    });

    return groups;
  };

  useEffect(() => {
    if (client) {
      fetchClientActivities();
    }
  }, [client]);

  useEffect(() => {
    applyFilters();
  }, [activeFilters, activities]);

  // Activity Detail Modal Component
  const ActivityDetailModal = ({ activity, isOpen, onClose }) => {
    if (!isOpen || !activity) return null;

    const typeConfig = activityTypes[activity.type] || activityTypes.note;
    const IconComponent = typeConfig.icon;

    // Find the related project for better context
    const relatedProject = projects?.find(p => p.id === activity.data.project_id || p.name === activity.data.project_name);

    const handleViewProject = () => {
      if (relatedProject && onSelectProject) {
        onSelectProject(relatedProject);
        onClose();
      }
    };

    const handleViewTask = () => {
      if (activity.type === 'task' && onOpenTask) {
        onOpenTask(activity.data);
        onClose();
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 ${typeConfig.color} rounded-full flex items-center justify-center`}>
                <IconComponent className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{activity.title}</h2>
                <p className="text-sm text-gray-500">{typeConfig.label} • {formatTimestamp(activity.timestamp)}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Modal Content */}
          <div className="p-6">
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Description</h3>
              <p className="text-gray-900">{activity.description}</p>
            </div>

            {activity.author && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Author</h3>
                <p className="text-gray-900">
                  {activity.author}
                  {activity.authorEmail && ` (${activity.authorEmail})`}
                </p>
              </div>
            )}

            {activity.type === 'email' && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Email Details</h3>
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <div><span className="font-medium">Subject:</span> {activity.data?.subject || activity.title || 'Email Message'}</div>
                  {activity.data?.from && <div><span className="font-medium">From:</span> {activity.data.from}</div>}
                  {activity.data?.to && <div><span className="font-medium">To:</span> {Array.isArray(activity.data.to) ? activity.data.to.join(', ') : activity.data.to}</div>}
                  {activity.data?.thread_key && <div><span className="font-medium">Thread:</span> <span className="font-mono text-xs">{activity.data.thread_key}</span></div>}
                  <div className="pt-2 border-t border-gray-200">
                    <span className="font-medium">Body Preview:</span>
                    <p className="mt-1 text-sm text-gray-700 whitespace-pre-wrap break-words">
                      {activity.data?.body_content || activity.data?.body || activity.data?.snippet || activity.description || 'No content available'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Enhanced Meeting Details */}
            {activity.type === 'meeting' && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Meeting Details</h3>
                <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                  {/* Meeting Name */}
                  <div className="mb-3">
                    <span className="font-medium text-gray-800">Meeting Name:</span> 
                    <p className="text-gray-900 mt-1">{activity.title || 'Untitled Meeting'}</p>
                  </div>

                  {/* Meeting Summary Placeholder */}
                  <div className="mb-3">
                    <span className="font-medium text-gray-800">Summary:</span> 
                    <p className="text-gray-600 italic mt-1">Meeting summary will be available soon</p>
                  </div>

                  {/* Enhanced Attendees Display */}
                  {activity.attendees && activity.attendees.length > 0 && (
                    <div className="mb-3">
                      <span className="font-medium text-gray-800">Attendees:</span>
                      {(() => {
                        // Sort attendees: clients first, then workspace users
                        const sortedAttendees = [...activity.attendees].sort((a, b) => {
                          // Check if attendee is likely a client (based on email domain matching client's domain)
                          const isAClient = client?.email && a.email && 
                            a.email.split('@')[1] === client.email.split('@')[1];
                          const isBClient = client?.email && b.email && 
                            b.email.split('@')[1] === client.email.split('@')[1];
                          
                          // Clients come first
                          if (isAClient && !isBClient) return -1;
                          if (!isAClient && isBClient) return 1;
                          
                          // Then alphabetically
                          return a.name.localeCompare(b.name);
                        });

                        // Create attendee display with truncation logic
                        const MAX_DISPLAY_LENGTH = 60; // Maximum characters for attendee list in modal
                        let displayText = '';
                        let truncated = false;
                        
                        for (let i = 0; i < sortedAttendees.length; i++) {
                          const attendeeName = sortedAttendees[i].name;
                          const nextText = i === 0 ? attendeeName : `, ${attendeeName}`;
                          
                          if ((displayText + nextText).length > MAX_DISPLAY_LENGTH) {
                            const remaining = sortedAttendees.length - i;
                            displayText += ` and ${remaining} more...`;
                            truncated = true;
                            break;
                          }
                          
                          displayText += nextText;
                        }

                        return (
                          <div className="mt-1">
                            <p className="text-gray-900">{displayText}</p>
                            {!truncated && sortedAttendees.length > 1 && (
                              <p className="text-xs text-gray-500 mt-1">
                                Multiple team members attended this meeting
                              </p>
                            )}
                            {truncated && (
                              <p className="text-xs text-gray-500 mt-1">
                                Click attendee names above to see full list
                              </p>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Technical Meeting Details */}
                  <div className="border-t border-gray-200 pt-3">
                    {activity.data.meeting_id && (
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium">Meeting ID:</span> 
                        <span className="font-mono text-sm">{activity.data.meeting_id}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium">Duration:</span> 
                      <span>{activity.data.duration_minutes || activity.data.duration || 0} minutes</span>
                    </div>
                    {activity.data.integration_type && (
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium">Platform:</span> 
                        <span className="capitalize">{activity.data.integration_type.replace('_', ' ')}</span>
                      </div>
                    )}
                    {activity.data.has_recording && (
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Recording:</span> 
                        <span className="text-green-600">✅ Available</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Enhanced Task Details */}
            {activity.type === 'task' && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Task Details</h3>
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Status:</span> 
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      activity.data.status === 'Done' ? 'bg-green-100 text-green-800' :
                      activity.data.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                      activity.data.status === 'Backlog' ? 'bg-gray-100 text-gray-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {activity.data.status}
                    </span>
                  </div>
                  {activity.data.priority && (
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Priority:</span> 
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        activity.data.priority === 'High' ? 'bg-red-100 text-red-800' :
                        activity.data.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {activity.data.priority}
                      </span>
                    </div>
                  )}
                  {activity.data.due_date && (
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Due Date:</span> 
                      <span>{new Date(activity.data.due_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  {activity.data.description && (
                    <div className="mt-3">
                      <span className="font-medium">Full Description:</span>
                      <p className="mt-1 text-sm text-gray-600">{activity.data.description}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Enhanced Project Details */}
            {activity.type === 'project' && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Project Details</h3>
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Status:</span> 
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      activity.data.status === 'Active' || activity.data.status === 'open' ? 'bg-green-100 text-green-800' :
                      activity.data.status === 'Completed' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {activity.data.status || 'Active'}
                    </span>
                  </div>
                  {activity.data.due_date && (
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Due Date:</span> 
                      <span>{new Date(activity.data.due_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  {relatedProject && (
                    <div className="mt-3">
                      <span className="font-medium">Project Name:</span>
                      <p className="mt-1 text-sm text-gray-600">{relatedProject.name}</p>
                      {relatedProject.description && (
                        <div className="mt-2">
                          <span className="font-medium">Description:</span>
                          <p className="mt-1 text-sm text-gray-600">{relatedProject.description}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Related Project/Task Info */}
            {activity.data.task_name && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Related Task</h3>
                <p className="text-gray-900">{activity.data.task_name}</p>
              </div>
            )}

            {activity.data.project_name && activity.type !== 'project' && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Related Project</h3>
                <p className="text-gray-900">{activity.data.project_name}</p>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
              {activity.type === 'task' && onOpenTask && (
                <button 
                  onClick={handleViewTask}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  View Full Task
                </button>
              )}
              {(activity.type === 'project' || relatedProject) && onSelectProject && (
                <button 
                  onClick={handleViewProject}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  View Project
                </button>
              )}
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-2 text-gray-600">Loading activity...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24">
      {/* Filter Icons and Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h2 className="text-xl font-semibold text-gray-900">Activity Timeline</h2>
          
          {/* Filter Icons */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => toggleFilter('all')}
              className={`flex items-center space-x-1 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                activeFilters.includes('all') 
                  ? 'bg-gray-800 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span>All</span>
            </button>
            
            {Object.entries(activityTypes).map(([type, config]) => {
              const IconComponent = config.icon;
              const isActive = activeFilters.includes(type);
              
              return (
                <button
                  key={type}
                  onClick={() => toggleFilter(type)}
                  className={`p-2 rounded-full transition-colors ${
                    isActive 
                      ? `${config.color} text-white shadow-md` 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title={config.label}
                >
                  <IconComponent className="w-4 h-4" />
                </button>
              );
            })}
          </div>
        </div>

        {/* Filter Menu Button */}
        <div className="relative">
          <button
            onClick={() => setShowFilterMenu(!showFilterMenu)}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Filter className="w-4 h-4" />
            <span>Filter</span>
          </button>
          
          {showFilterMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-10">
              <div className="px-4 py-2 text-sm font-medium text-gray-900 border-b border-gray-100">
                Show/Hide Items
              </div>
              {Object.entries(activityTypes).map(([type, config]) => (
                <button
                  key={type}
                  onClick={() => toggleFilter(type)}
                  className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                    activeFilters.includes(type) 
                      ? 'bg-purple-50 text-purple-700' 
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{config.label}</span>
                    {activeFilters.includes(type) && (
                      <CheckSquare className="w-4 h-4 text-purple-600" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Activity Feed */}
      <div className="space-y-4 max-h-[600px] overflow-y-auto">
        {filteredActivities.length > 0 ? (
          <div className="space-y-4">
            {getThreadedGroups(filteredActivities).map((entry) => {
              if (entry.kind === 'single') {
                return <ActivityItem key={entry.activity.id} activity={entry.activity} />;
              }

              const threadOpen = !!expandedThreads[entry.threadKey];
              return (
                <div key={`thread-${entry.threadKey}`} className="border border-gray-200 rounded-lg bg-white p-3">
                  <button
                    onClick={() => setExpandedThreads(prev => ({ ...prev, [entry.threadKey]: !prev[entry.threadKey] }))}
                    className="w-full text-left flex items-center justify-between"
                  >
                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        {entry.type === 'email' ? 'Email Thread' : 'Message Thread'}
                        <span className="ml-2 text-xs text-gray-500">({entry.count} messages)</span>
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {entry.latest.data?.subject || entry.latest.title || 'No subject'}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Latest: {formatTimestamp(entry.latest.timestamp)}
                      </div>
                    </div>
                    <div className="text-purple-600 text-sm">{threadOpen ? 'Collapse' : 'Expand'}</div>
                  </button>

                  {threadOpen && (
                    <div className="mt-3 space-y-3 border-t border-gray-100 pt-3">
                      {entry.items.map((activity) => (
                        <ActivityItem key={activity.id} activity={activity} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Activity</h3>
            <p className="text-gray-600">
              {activeFilters.includes('all') 
                ? `No activity found for ${client.name}.`
                : `No ${activeFilters.join(', ')} activity found for ${client.name}.`
              }
            </p>
          </div>
        )}
      </div>

      {/* Activity Detail Modal */}
      <ActivityDetailModal 
        activity={selectedActivity}
        isOpen={showActivityModal}
        onClose={() => {
          setShowActivityModal(false);
          setSelectedActivity(null);
        }}
      />
    </div>
  );
};

export default ClientActivityFeed;